import React from 'react';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  Button,
  Grid,
  Divider,
  Box,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Card,
  CardContent,
  Collapse,
  IconButton,
  TextField,
  InputAdornment,
  TablePagination,
  TableSortLabel,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress
} from '@material-ui/core';
import {
  Category as CategoryIcon,
  LocationOn as LocationIcon,
  TimeToLeave as VehicleIcon,
  Error as WarningIcon,
  InfoOutlined as InfoIcon,
  LocalGasStation as GasIcon,
  Build as BuildIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  DirectionsCar as CarIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  Sync as SyncIcon,
  Link as LinkIcon
} from '@material-ui/icons';
import { makeStyles } from '@material-ui/core/styles';
import axios from 'axios';

const useStyles = makeStyles((theme) => ({
  sectionTitle: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1),
    display: 'flex',
    alignItems: 'center',
  },
  icon: {
    marginRight: theme.spacing(1),
    color: theme.palette.primary.main,
  },
  detailItem: {
    marginBottom: theme.spacing(1),
  },
  detailLabel: {
    fontWeight: 'bold',
    color: theme.palette.text.secondary,
  },
  detailValue: {
    color: theme.palette.text.primary,
  },
  warningChip: {
    backgroundColor: theme.palette.warning.light,
    color: theme.palette.warning.contrastText,
    margin: theme.spacing(1),
  },
  divider: {
    margin: theme.spacing(2, 0),
  },
  compatibilityTable: {
    marginTop: theme.spacing(2),
  },
  actionButton: {
    margin: theme.spacing(1),
  },

  vehicleCard: {
    marginBottom: theme.spacing(1),
    '&:hover': {
      boxShadow: theme.shadows[2]
    }
  },
  vehicleGroupHeader: {
    backgroundColor: theme.palette.grey[100],
    padding: theme.spacing(1.5, 2),
    marginBottom: theme.spacing(1),
    borderRadius: theme.shape.borderRadius,
    cursor: 'pointer',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    '&:hover': {
      backgroundColor: theme.palette.grey[200]
    }
  },
  vehicleCount: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    borderRadius: 12,
    padding: '2px 8px',
    fontSize: '0.75rem',
    marginLeft: theme.spacing(1)
  },
  engineChip: {
    marginLeft: theme.spacing(1),
    height: 24,
    fontSize: '0.75rem'
  },
  fuelChip: {
    marginLeft: theme.spacing(0.5),
    height: 24,
    fontSize: '0.75rem',
    color: 'white'
  },
  vehicleDetails: {
    marginTop: theme.spacing(1),
    padding: theme.spacing(1),
    backgroundColor: theme.palette.grey[50],
    borderRadius: theme.shape.borderRadius
  },
  engineInfo: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: theme.spacing(1)
  },
  vehicleFilters: {
    marginBottom: theme.spacing(2),
    display: 'flex',
    gap: theme.spacing(2),
    flexWrap: 'wrap',
    alignItems: 'center'
  },
  searchField: {
    flex: '1 1 300px',
    minWidth: 250
  },
  filterSelect: {
    minWidth: 150
  },
  statsChip: {
    margin: theme.spacing(0.5),
    fontWeight: 500
  },
  tableHeader: {
    backgroundColor: theme.palette.grey[100],
    '& th': {
      fontWeight: 600
    }
  }
}));

const isOreillyFormat = (vehicle) => {
  return vehicle.hasOwnProperty('yearStart') && vehicle.hasOwnProperty('yearEnd');
};

// Helper to get fuel type icon/color
const getFuelTypeColor = (fuelType) => {
  switch(fuelType?.toUpperCase()) {
    case 'DIESEL': return '#1976d2';
    case 'GAS': return '#4caf50';
    case 'HYBRID': return '#ff9800';
    case 'ELECTRIC': return '#9c27b0';
    case 'FLEX': return '#607d8b';
    default: return '#757575';
  }
};

const InventoryItemDetailsDialog = ({ open, onClose, item, onEdit, onDelete }) => {
  const classes = useStyles();
  const [expandedMakes, setExpandedMakes] = React.useState({});

  // Vehicle table state
  const [vehicleSearch, setVehicleSearch] = React.useState('');
  const [makeFilter, setMakeFilter] = React.useState('All');
  const [fuelFilter, setFuelFilter] = React.useState('All');
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(25);
  const [orderBy, setOrderBy] = React.useState('make');
  const [order, setOrder] = React.useState('asc');

  // O'Reilly URL state
  const [oreillyUrl, setOreillyUrl] = React.useState('');
  const [isFetchingFitment, setIsFetchingFitment] = React.useState(false);
  const [fetchMessage, setFetchMessage] = React.useState(null);

  // IMPORTANT: Always call hooks in the same order - compute these even if item is null
  // Get unique makes and fuel types for filters
  const uniqueMakes = React.useMemo(() => {
    if (!item?.compatibleVehicles) return [];
    const makes = [...new Set(item.compatibleVehicles.map(v => v.make).filter(Boolean))];
    return makes.sort();
  }, [item?.compatibleVehicles]);

  const uniqueFuelTypes = React.useMemo(() => {
    if (!item?.compatibleVehicles) return [];
    const fuels = [...new Set(item.compatibleVehicles.map(v => v.fuelType?.toUpperCase()).filter(Boolean))];
    return fuels.sort();
  }, [item?.compatibleVehicles]);

  // Initialize O'Reilly URL from item
  React.useEffect(() => {
    if (item?.oreillyUrl) {
      setOreillyUrl(item.oreillyUrl);
    } else {
      setOreillyUrl('');
    }
  }, [item]);

  // Early return AFTER all hooks
  if (!item) return null;

  const needsReorder = item.quantity <= item.minStockLevel;

  // Vehicle filtering and sorting logic
  const getFilteredAndSortedVehicles = () => {
    if (!item.compatibleVehicles || item.compatibleVehicles.length === 0) {
      return [];
    }

    let filtered = [...item.compatibleVehicles];

    // Apply search filter
    if (vehicleSearch) {
      const searchLower = vehicleSearch.toLowerCase();
      const searchYear = parseInt(vehicleSearch);
      const isYearSearch = !isNaN(searchYear) && searchYear > 1900 && searchYear < 2100;

      filtered = filtered.filter(vehicle => {
        // Text-based search for make, model, engine
        const textMatch =
          (vehicle.make?.toLowerCase().includes(searchLower)) ||
          (vehicle.model?.toLowerCase().includes(searchLower)) ||
          (vehicle.engineSize?.toLowerCase().includes(searchLower));

        // Smart year search - check if search year falls within range
        let yearMatch = false;
        if (isYearSearch) {
          if (vehicle.yearStart && vehicle.yearEnd) {
            // O'Reilly format: check if search year is in range
            yearMatch = searchYear >= vehicle.yearStart && searchYear <= vehicle.yearEnd;
          } else if (vehicle.year) {
            // Single year format: exact match
            yearMatch = vehicle.year === searchYear;
          }
        } else {
          // Non-year search: check year fields as strings
          yearMatch =
            (vehicle.yearStart?.toString().includes(searchLower)) ||
            (vehicle.yearEnd?.toString().includes(searchLower)) ||
            (vehicle.year?.toString().includes(searchLower));
        }

        return textMatch || yearMatch;
      });
    }

    // Apply make filter
    if (makeFilter !== 'All') {
      filtered = filtered.filter(vehicle => vehicle.make === makeFilter);
    }

    // Apply fuel type filter
    if (fuelFilter !== 'All') {
      filtered = filtered.filter(vehicle => vehicle.fuelType?.toUpperCase() === fuelFilter);
    }

    // Sort
    filtered.sort((a, b) => {
      let aValue, bValue;

      switch (orderBy) {
        case 'make':
          aValue = a.make || '';
          bValue = b.make || '';
          break;
        case 'model':
          aValue = a.model || '';
          bValue = b.model || '';
          break;
        case 'year':
          aValue = a.yearStart || a.year || 0;
          bValue = b.yearStart || b.year || 0;
          break;
        case 'engine':
          aValue = a.engineSize || '';
          bValue = b.engineSize || '';
          break;
        case 'fuel':
          aValue = a.fuelType || '';
          bValue = b.fuelType || '';
          break;
        default:
          return 0;
      }

      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return order === 'asc'
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      } else {
        return order === 'asc' ? aValue - bValue : bValue - aValue;
      }
    });

    return filtered;
  };

  const filteredVehicles = getFilteredAndSortedVehicles();
  const paginatedVehicles = filteredVehicles.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchChange = (event) => {
    setVehicleSearch(event.target.value);
    setPage(0); // Reset to first page when searching
  };

  const handleMakeFilterChange = (event) => {
    setMakeFilter(event.target.value);
    setPage(0);
  };

  const handleFuelFilterChange = (event) => {
    setFuelFilter(event.target.value);
    setPage(0);
  };

  const handleFetchFitment = async () => {
    if (!oreillyUrl || !oreillyUrl.trim()) {
      setFetchMessage({ type: 'error', text: 'Please enter an O\'Reilly URL' });
      return;
    }

    setIsFetchingFitment(true);
    setFetchMessage(null);

    try {
      const response = await axios.put(`/api/shop/inventory/${item._id}/update-from-url`, {
        url: oreillyUrl
      });

      if (response?.data?.success) {
        setFetchMessage({
          type: 'success',
          text: `Successfully fetched fitment data! Found ${response.data.vehicleCount || 0} compatible vehicles.`
        });
        // Close and reopen to refresh data
        setTimeout(() => {
          window.location.reload(); // Simple refresh, you might want to use a more elegant approach
        }, 1500);
      } else {
        setFetchMessage({
          type: 'error',
          text: response?.data?.error || 'Failed to fetch fitment data'
        });
      }
    } catch (error) {
      console.error('Error fetching fitment:', error);
      const errorMessage = error?.response?.data?.error
        || error?.message
        || 'Error fetching fitment data from URL';
      setFetchMessage({
        type: 'error',
        text: errorMessage
      });
    } finally {
      setIsFetchingFitment(false);
    }
  };

  const handleSaveUrl = async () => {
    try {
      await axios.put(`/api/shop/inventory/${item._id}`, {
        oreillyUrl: oreillyUrl
      });
      setFetchMessage({ type: 'success', text: 'URL saved successfully!' });
      setTimeout(() => setFetchMessage(null), 3000);
    } catch (error) {
      console.error('Error saving URL:', error);
      setFetchMessage({ type: 'error', text: 'Failed to save URL' });
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Grid container alignItems="center" justifyContent="space-between">
          <Grid item>
            <Typography variant="h6">{item.title}</Typography>
          </Grid>
          <Grid item>
            {needsReorder && (
              <Chip
                icon={<WarningIcon />}
                label="Low Stock"
                className={classes.warningChip}
                size="small"
              />
            )}
          </Grid>
        </Grid>
      </DialogTitle>
      <DialogContent>
        {/* Basic Details */}
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Box className={classes.detailItem}>
              <Typography variant="body2" className={classes.detailLabel}>
                Barcode
              </Typography>
              <Typography variant="body1" className={classes.detailValue}>
                {item.barcode || 'N/A'}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Box className={classes.detailItem}>
              <Typography variant="body2" className={classes.detailLabel}>
                Part Number
              </Typography>
              <Typography variant="body1" className={classes.detailValue}>
                {item.partNumber || 'N/A'}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Box className={classes.detailItem}>
              <Typography variant="body2" className={classes.detailLabel}>
                Brand
              </Typography>
              <Typography variant="body1" className={classes.detailValue}>
                {item.brand || 'N/A'}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Box className={classes.detailItem}>
              <Typography variant="body2" className={classes.detailLabel}>
                Model
              </Typography>
              <Typography variant="body1" className={classes.detailValue}>
                {item.model || 'N/A'}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12}>
            <Box className={classes.detailItem}>
              <Typography variant="body2" className={classes.detailLabel}>
                Description
              </Typography>
              <Typography variant="body1" className={classes.detailValue}>
                {item.description || 'No description available'}
              </Typography>
            </Box>
          </Grid>
        </Grid>

        <Divider className={classes.divider} />

        {/* Category and Location */}
        <Typography variant="h6" className={classes.sectionTitle}>
          <CategoryIcon className={classes.icon} />
          Classification
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Box className={classes.detailItem}>
              <Typography variant="body2" className={classes.detailLabel}>
                Category
              </Typography>
              <Typography variant="body1" className={classes.detailValue}>
                {item.category?.name || 'N/A'}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Box className={classes.detailItem}>
              <Typography variant="body2" className={classes.detailLabel}>
                Subcategory
              </Typography>
              <Typography variant="body1" className={classes.detailValue}>
                {item.subcategory || 'N/A'}
              </Typography>
            </Box>
          </Grid>
        </Grid>

        <Typography variant="h6" className={classes.sectionTitle}>
          <LocationIcon className={classes.icon} />
          Storage Details
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <Box className={classes.detailItem}>
              <Typography variant="body2" className={classes.detailLabel}>
                Quantity in Stock
              </Typography>
              <Typography variant="body1" className={classes.detailValue}>
                {item.quantity}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Box className={classes.detailItem}>
              <Typography variant="body2" className={classes.detailLabel}>
                Minimum Stock Level
              </Typography>
              <Typography variant="body1" className={classes.detailValue}>
                {item.minStockLevel}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Box className={classes.detailItem}>
              <Typography variant="body2" className={classes.detailLabel}>
                Storage Location
              </Typography>
              <Typography variant="body1" className={classes.detailValue}>
                {item.location || 'N/A'}
              </Typography>
            </Box>
          </Grid>
        </Grid>

        <Divider className={classes.divider} />

        {/* Supplier Information */}
        <Typography variant="h6" className={classes.sectionTitle}>
          <InfoIcon className={classes.icon} />
          Supplier & Ordering Information
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <Box className={classes.detailItem}>
              <Typography variant="body2" className={classes.detailLabel}>
                Preferred Supplier
              </Typography>
              <Typography variant="body1" className={classes.detailValue}>
                {item.preferredSupplier || 'N/A'}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Box className={classes.detailItem}>
              <Typography variant="body2" className={classes.detailLabel}>
                Supplier Part Number
              </Typography>
              <Typography variant="body1" className={classes.detailValue}>
                {item.supplierPartNumber || 'N/A'}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Box className={classes.detailItem}>
              <Typography variant="body2" className={classes.detailLabel}>
                Cost Price
              </Typography>
              <Typography variant="body1" className={classes.detailValue}>
                {item.costPrice ? `$${item.costPrice.toFixed(2)}` : 'N/A'}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Box className={classes.detailItem}>
              <Typography variant="body2" className={classes.detailLabel}>
                Last Ordered
              </Typography>
              <Typography variant="body1" className={classes.detailValue}>
                {item.lastOrdered 
                  ? new Date(item.lastOrdered).toLocaleDateString() 
                  : 'N/A'}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Box className={classes.detailItem}>
              <Typography variant="body2" className={classes.detailLabel}>
                Last Used
              </Typography>
              <Typography variant="body1" className={classes.detailValue}>
                {item.lastUsed
                  ? new Date(item.lastUsed).toLocaleDateString()
                  : 'N/A'}
              </Typography>
            </Box>
          </Grid>
        </Grid>

        <Divider className={classes.divider} />

        {/* O'Reilly URL Section */}
        <Typography variant="h6" className={classes.sectionTitle}>
          <LinkIcon className={classes.icon} />
          Part Fitment Data
        </Typography>

        <Grid container spacing={2} alignItems="flex-start">
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="O'Reilly Auto URL"
              variant="outlined"
              size="small"
              value={oreillyUrl}
              onChange={(e) => setOreillyUrl(e.target.value)}
              placeholder="https://www.oreillyauto.com/detail/..."
              helperText="Enter the O'Reilly product URL to fetch vehicle compatibility data"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LinkIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <Box display="flex" gap={1} alignItems="center">
              <Button
                variant="contained"
                color="primary"
                startIcon={isFetchingFitment ? <CircularProgress size={20} color="inherit" /> : <SyncIcon />}
                onClick={handleFetchFitment}
                disabled={isFetchingFitment || !oreillyUrl?.trim()}
              >
                {isFetchingFitment ? 'Fetching...' : 'Fetch Fitment Data'}
              </Button>
              <Button
                variant="outlined"
                color="primary"
                onClick={handleSaveUrl}
                disabled={isFetchingFitment}
              >
                Save URL
              </Button>
              {fetchMessage && (
                <Chip
                  label={fetchMessage.text}
                  color={fetchMessage.type === 'success' ? 'primary' : 'secondary'}
                  size="small"
                />
              )}
            </Box>
          </Grid>
        </Grid>

        {/* {item.compatibleVehicles && item.compatibleVehicles.length > 0 && (
          <>
            <Divider className={classes.divider} />
            <Typography variant="h6" className={classes.sectionTitle}>
              <VehicleIcon className={classes.icon} />
              Compatible Vehicles ({item.compatibleVehicles.length})
            </Typography>
            
            {(() => {
              // Group vehicles by make and model for better organization
              const vehicleGroups = {};
              item.compatibleVehicles.forEach(vehicle => {
                const makeKey = vehicle.make || 'Unknown';
                if (!vehicleGroups[makeKey]) {
                  vehicleGroups[makeKey] = {};
                }
                const modelKey = vehicle.model || 'Unknown';
                if (!vehicleGroups[makeKey][modelKey]) {
                  vehicleGroups[makeKey][modelKey] = [];
                }
                vehicleGroups[makeKey][modelKey].push(vehicle);
              });

              const toggleMake = (make) => {
                setExpandedMakes(prev => ({
                  ...prev,
                  [make]: !prev[make]
                }));
              };

              return (
                <Box style={{ marginTop: 16 }}>
                  {Object.keys(vehicleGroups).sort().map(make => {
                    const isExpanded = expandedMakes[make] !== false;
                    const vehicleCount = Object.values(vehicleGroups[make])
                      .reduce((sum, models) => sum + models.length, 0);
                    
                    return (
                      <Box key={make} style={{ marginBottom: 8 }}>
                        <div 
                          className={classes.vehicleGroupHeader}
                          onClick={() => toggleMake(make)}
                        >
                          <Box display="flex" alignItems="center">
                            <CarIcon style={{ marginRight: 8 }} />
                            <Typography variant="subtitle1" style={{ fontWeight: 500 }}>
                              {make}
                            </Typography>
                            <span className={classes.vehicleCount}>{vehicleCount}</span>
                          </Box>
                          {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </div>
                        
                        <Collapse in={isExpanded}>
                          <Box style={{ marginLeft: 16 }}>
                            {Object.keys(vehicleGroups[make]).sort().map(model => (
                              <Box key={`${make}-${model}`} style={{ marginBottom: 8 }}>
                                <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                                  {model}
                                </Typography>
                                
                                {vehicleGroups[make][model].map((vehicle, idx) => {
                                  const isOreilly = isOreillyFormat(vehicle);
                                  
                                  return (
                                    <Card 
                                      key={`${make}-${model}-${idx}`} 
                                      variant="outlined" 
                                      className={classes.vehicleCard}
                                    >
                                      <CardContent style={{ padding: 12 }}>
                                        <Grid container spacing={1}>
                                          <Grid item xs={12}>
                                            <Box className={classes.engineInfo}>
                                              <Typography variant="body2">
                                                <strong>
                                                  {isOreilly 
                                                    ? `${vehicle.yearStart}-${vehicle.yearEnd}` 
                                                    : vehicle.year || 'N/A'
                                                  }
                                                </strong>
                                              </Typography>
                                              
                                              {vehicle.engineSize && (
                                                <Chip
                                                  size="small"
                                                  icon={<BuildIcon style={{ fontSize: 16 }} />}
                                                  label={vehicle.engineSize}
                                                  className={classes.engineChip}
                                                  variant="outlined"
                                                />
                                              )}
                                              
                                              {vehicle.fuelType && (
                                                <Chip
                                                  size="small"
                                                  icon={<GasIcon style={{ fontSize: 16 }} />}
                                                  label={vehicle.fuelType}
                                                  className={classes.fuelChip}
                                                  style={{ 
                                                    backgroundColor: getFuelTypeColor(vehicle.fuelType)
                                                  }}
                                                />
                                              )}
                                            </Box>
                                          </Grid>
                                          
                                          {isOreilly && (vehicle.vinCode || vehicle.engineCode) && (
                                            <Grid item xs={12}>
                                              <Box className={classes.vehicleDetails}>
                                                <Grid container spacing={2}>
                                                  {vehicle.vinCode && (
                                                    <Grid item xs={6}>
                                                      <Typography variant="caption" color="textSecondary">
                                                        VIN Code (8th): <strong>{vehicle.vinCode}</strong>
                                                      </Typography>
                                                    </Grid>
                                                  )}
                                                  {vehicle.engineCode && (
                                                    <Grid item xs={6}>
                                                      <Typography variant="caption" color="textSecondary">
                                                        Engine: <strong>{vehicle.engineCode}</strong>
                                                      </Typography>
                                                    </Grid>
                                                  )}
                                                </Grid>
                                              </Box>
                                            </Grid>
                                          )}
                                          
                                          {(vehicle.description || vehicle.notes) && (
                                            <Grid item xs={12}>
                                              <Typography variant="caption" color="textSecondary">
                                                {vehicle.description || vehicle.notes}
                                              </Typography>
                                            </Grid>
                                          )}
                                        </Grid>
                                      </CardContent>
                                    </Card>
                                  );
                                })}
                              </Box>
                            ))}
                          </Box>
                        </Collapse>
                      </Box>
                    );
                  })}
                </Box>
              );
            })()}
          </>
        )} */}

        {/* Vehicle Compatibility - Enhanced Table with Filtering & Sorting */}
        {item.compatibleVehicles && item.compatibleVehicles.length > 0 && (
          <>
            <Divider className={classes.divider} />
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6" className={classes.sectionTitle}>
                <VehicleIcon className={classes.icon} />
                Compatible Vehicles
              </Typography>
              <Box>
                <Chip
                  label={`Total: ${item.compatibleVehicles.length}`}
                  className={classes.statsChip}
                  color="primary"
                  size="small"
                />
                <Chip
                  label={`Makes: ${uniqueMakes.length}`}
                  className={classes.statsChip}
                  size="small"
                />
                {vehicleSearch || makeFilter !== 'All' || fuelFilter !== 'All' ? (
                  <Chip
                    label={`Filtered: ${filteredVehicles.length}`}
                    className={classes.statsChip}
                    color="secondary"
                    size="small"
                  />
                ) : null}
              </Box>
            </Box>

            {/* Filters */}
            <Box className={classes.vehicleFilters}>
              <TextField
                className={classes.searchField}
                variant="outlined"
                size="small"
                placeholder="Search make, model, engine, year..."
                value={vehicleSearch}
                onChange={handleSearchChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />

              <FormControl variant="outlined" size="small" className={classes.filterSelect}>
                <InputLabel>Make</InputLabel>
                <Select
                  value={makeFilter}
                  onChange={handleMakeFilterChange}
                  label="Make"
                >
                  <MenuItem value="All">All Makes</MenuItem>
                  {uniqueMakes.map(make => (
                    <MenuItem key={make} value={make}>{make}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl variant="outlined" size="small" className={classes.filterSelect}>
                <InputLabel>Fuel Type</InputLabel>
                <Select
                  value={fuelFilter}
                  onChange={handleFuelFilterChange}
                  label="Fuel Type"
                >
                  <MenuItem value="All">All Fuels</MenuItem>
                  {uniqueFuelTypes.map(fuel => (
                    <MenuItem key={fuel} value={fuel}>{fuel}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            {/* Table */}
            <TableContainer component={Paper} className={classes.compatibilityTable}>
              <Table size="small">
                <TableHead className={classes.tableHeader}>
                  <TableRow>
                    <TableCell>
                      <TableSortLabel
                        active={orderBy === 'year'}
                        direction={orderBy === 'year' ? order : 'asc'}
                        onClick={() => handleRequestSort('year')}
                      >
                        Years
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={orderBy === 'make'}
                        direction={orderBy === 'make' ? order : 'asc'}
                        onClick={() => handleRequestSort('make')}
                      >
                        Make
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={orderBy === 'model'}
                        direction={orderBy === 'model' ? order : 'asc'}
                        onClick={() => handleRequestSort('model')}
                      >
                        Model
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={orderBy === 'engine'}
                        direction={orderBy === 'engine' ? order : 'asc'}
                        onClick={() => handleRequestSort('engine')}
                      >
                        Engine
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={orderBy === 'fuel'}
                        direction={orderBy === 'fuel' ? order : 'asc'}
                        onClick={() => handleRequestSort('fuel')}
                      >
                        Fuel Type
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>Codes</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedVehicles.length > 0 ? (
                    paginatedVehicles.map((vehicle, index) => {
                      const isOreilly = isOreillyFormat(vehicle);
                      return (
                        <TableRow key={index} hover>
                          <TableCell>
                            {isOreilly
                              ? `${vehicle.yearStart}-${vehicle.yearEnd}`
                              : vehicle.year || 'N/A'
                            }
                          </TableCell>
                          <TableCell>{vehicle.make || 'N/A'}</TableCell>
                          <TableCell>{vehicle.model || 'N/A'}</TableCell>
                          <TableCell>{vehicle.engineSize || 'N/A'}</TableCell>
                          <TableCell>
                            {vehicle.fuelType ? (
                              <Chip
                                size="small"
                                label={vehicle.fuelType}
                                style={{
                                  backgroundColor: getFuelTypeColor(vehicle.fuelType),
                                  color: 'white',
                                  fontSize: '0.7rem',
                                  height: 20
                                }}
                              />
                            ) : 'N/A'}
                          </TableCell>
                          <TableCell>
                            {(vehicle.vinCode || vehicle.engineCode) ? (
                              <div style={{ fontSize: '0.75rem' }}>
                                {vehicle.vinCode && <div>VIN: {vehicle.vinCode}</div>}
                                {vehicle.engineCode && <div>Eng: {vehicle.engineCode}</div>}
                              </div>
                            ) : 'N/A'}
                          </TableCell>
                        </TableRow>
                      );
                    })
                  ) : (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        <Typography variant="body2" color="textSecondary">
                          No vehicles match your filters
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
              <TablePagination
                rowsPerPageOptions={[10, 25, 50, 100]}
                component="div"
                count={filteredVehicles.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </TableContainer>
          </>
        )}

        {/* Maintenance Information */}
        {(item.maintenanceInterval?.miles || item.maintenanceInterval?.months || item.replacementFrequency) && (
          <>
            <Divider className={classes.divider} />
            <Typography variant="h6" className={classes.sectionTitle}>
              <InfoIcon className={classes.icon} />
              Maintenance Information
            </Typography>
            <Grid container spacing={2}>
              {item.maintenanceInterval?.miles && (
                <Grid item xs={12} sm={4}>
                  <Box className={classes.detailItem}>
                    <Typography variant="body2" className={classes.detailLabel}>
                      Maintenance Interval (Miles)
                    </Typography>
                    <Typography variant="body1" className={classes.detailValue}>
                      {item.maintenanceInterval.miles.toLocaleString()} miles
                    </Typography>
                  </Box>
                </Grid>
              )}
              {item.maintenanceInterval?.months && (
                <Grid item xs={12} sm={4}>
                  <Box className={classes.detailItem}>
                    <Typography variant="body2" className={classes.detailLabel}>
                      Maintenance Interval (Months)
                    </Typography>
                    <Typography variant="body1" className={classes.detailValue}>
                      {item.maintenanceInterval.months} months
                    </Typography>
                  </Box>
                </Grid>
              )}
              {item.replacementFrequency && (
                <Grid item xs={12} sm={4}>
                  <Box className={classes.detailItem}>
                    <Typography variant="body2" className={classes.detailLabel}>
                      Replacement Frequency
                    </Typography>
                    <Typography variant="body1" className={classes.detailValue}>
                      {item.replacementFrequency}
                    </Typography>
                  </Box>
                </Grid>
              )}
            </Grid>
          </>
        )}

        {/* Notes */}
        {item.notes && (
          <>
            <Divider className={classes.divider} />
            <Typography variant="h6" className={classes.sectionTitle}>
              <InfoIcon className={classes.icon} />
              Notes
            </Typography>
            <Typography variant="body1">
              {item.notes}
            </Typography>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary">
          Close
        </Button>
        <Button 
          onClick={() => onEdit(item)}
          color="primary" 
          variant="outlined"
          className={classes.actionButton}
        >
          Edit
        </Button>
        <Button 
          onClick={() => onDelete(item)}
          color="secondary" 
          variant="outlined"
          className={classes.actionButton}
        >
          Delete
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default InventoryItemDetailsDialog;
